import type { ScpData } from "@hobbyshopify/backend-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { type Grade, gradePriceText } from "../core/primitives/grades";
import { type GradeOption, gradeEnterPicks, gradeOptionsFor, namedGradeIndex } from "./grade-options";

/**
 * The grade field: type it, or pick it from the graded list with prices.
 *
 * Both halves are needed. The stored value is **free-form text** — the model
 * writes "PSA 10", "psa10", "9.5", "SGC 98", and scan-core's `normalizeGrade`
 * parses whatever it finds — so a plain `<select>` would refuse readings the
 * pipeline produces every day, and a list can never cover the long tail of
 * grader names. But typing a grade blind is the wrong way to make the decision
 * this screen is actually about: the grade is part of the card's identity and
 * it picks the comp the price comes from, so the reviewer is choosing a price
 * as much as a label. The list carries the SportsCardsPro figure next to each
 * grade for that reason — the same numbers the eBay grid below shows, from the
 * same payload.
 */

export function GradeCombobox({
  value,
  onType,
  onCommit,
  onPick,
  scpData,
  placeholder,
  id,
  disabled = false,
}: {
  /** For a label to name the field. */
  id?: string;
  disabled?: boolean;
  value: string;
  onType: (grade: string) => void;
  /**
   * A typed grade the reviewer has finished with — fired on blur, not per
   * keystroke. Re-pricing on every character would quote "PSA 1" on the way to
   * "PSA 10", and half of those figures are an order of magnitude out.
   */
  onCommit?: (grade: string) => void;
  onPick: (grade: Grade) => void;
  scpData?: ScpData | null;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeRaw, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Typed-since-last-committed. A pick answers for itself through `onPick`, so
  // the blur that follows it must not re-announce the same change.
  const typed = useRef(false);
  // The reviewer moved the highlight since the list opened (arrows or pointer).
  const moved = useRef(false);

  const { options, reading } = useMemo(() => gradeOptionsFor(value), [value]);

  // A shrinking filter can leave the highlight past the end; clamping beats an
  // effect that resets it, which would fight the arrow keys on every render.
  const active = Math.min(activeRaw, options.length - 1);

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  // A list opened from its button leaves the input without focus, so no blur
  // closes it: a press anywhere else does.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away, true);
    return () => document.removeEventListener("pointerdown", away, true);
  }, [open]);

  const pick = (option: GradeOption) => {
    typed.current = false;
    onPick(option.grade);
    setOpen(false);
  };

  const commit = () => {
    if (!typed.current) return;
    typed.current = false;
    onCommit?.(value);
  };

  const show = (text: string) => {
    moved.current = false;
    setActive(namedGradeIndex(gradeOptionsFor(text).options, text));
    setOpen(true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return show(value);
      const step = e.key === "ArrowDown" ? 1 : -1;
      moved.current = true;
      return setActive((i) => (i + step + options.length) % options.length);
    }
    // Enter only commits a highlighted option while the list is open; otherwise
    // it belongs to the form, and stealing it would swallow a typed grade.
    if (e.key === "Enter") {
      const option = open ? options[active] : undefined;
      if (!option) return commit();
      e.preventDefault();
      if (gradeEnterPicks({ option, text: value, typed: typed.current, moved: moved.current, reading })) {
        pick(option);
      } else {
        setOpen(false);
        commit();
      }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        disabled={disabled}
        className="field pr-9"
        value={value}
        onChange={(e) => {
          typed.current = true;
          onType(e.target.value);
          show(e.target.value);
        }}
        onFocus={() => show(value)}
        // A tap on the field it already holds opens the list again, as after a
        // press elsewhere closed it while the keyboard stayed up.
        onClick={() => {
          if (!open) show(value);
        }}
        // Every control in the list preserves the input's focus on mousedown, so
        // losing focus really does mean the reviewer has moved on.
        onBlur={() => {
          setOpen(false);
          commit();
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="grade-options"
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label={open ? "Hide grades" : "Show grades"}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? setOpen(false) : show(value))}
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-faint hover:text-white"
      >
        {open ? "▴" : "▾"}
      </button>

      {open && options.length > 0 && (
        <ul
          id="grade-options"
          ref={listRef}
          // A scrolling list with no tab stops is one itself in Chrome; Tab leaves the field.
          tabIndex={-1}
          // A press on its scrollbar or a group's name keeps the input's focus, so the list stays open.
          onMouseDown={(e) => e.preventDefault()}
          // Over the Save bar (z-30), which would otherwise cover its lower rows.
          className="absolute z-40 mt-1 max-h-64 w-full overflow-y-auto rounded-btn border border-line bg-app-elevated py-1 shadow-lg"
        >
          {options.map((option, i) => {
            const priceText = gradePriceText(option.grade.key, scpData);
            // The group only earns a line when it changes: a header above every
            // row would double the length of a list read by scanning.
            const header = i === 0 || options[i - 1].group !== option.group ? option.group : null;
            return (
              <li key={option.grade.key}>
                {header && <p className="eyebrow px-3 pb-0.5 pt-2">{header}</p>}
                <button
                  type="button"
                  // The arrows move through the list: Tab leaves the field.
                  tabIndex={-1}
                  // Keeps focus on the input, so the container's blur never
                  // closes the list out from under the click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(option)}
                  // Only a pointer that moves takes the highlight: a list opened
                  // under a resting one would hand Enter whatever row it met.
                  onMouseMove={(e) => {
                    if (e.movementX === 0 && e.movementY === 0) return;
                    moved.current = true;
                    if (i !== active) setActive(i);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm ${
                    i === active ? "bg-surface-strong text-white" : "text-muted"
                  }`}
                >
                  <span>{option.grade.label}</span>
                  {/* Blank, not "—": a grade SportsCardsPro carries no figure for
                      is common, and a column of dashes reads as an error. */}
                  <span className={priceText ? "text-accent" : "text-faint"}>{priceText}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
